import React, { useState, useEffect } from 'react';
import { Food, CarbUnit } from '../types';
import { calculateCarbsForPortion, formatCarbs } from '../utils/carbCalculations';
import { calculateFPU } from '../utils/fpuCalculations';
import { usePreferences } from '../contexts/PreferencesContext';
import { AddCustomFoodModal } from './AddCustomFoodModal';
import { FoodImage } from './FoodImage';
import { getPhotoData } from '../utils/photoUtils';
import './SearchResults.css';

// Helper function to generate source URL for OpenFoodFacts products
const getSourceUrl = (food: Food): string | undefined => {
  if (food.id.startsWith('off_') && food.ean) {
    return `https://world.openfoodfacts.org/product/${food.ean}`;
  }
  return food.sourceUrl; // Fallback to existing sourceUrl if present
};

interface SearchResultsProps {
  foods: Food[];
  carbUnit: CarbUnit;
  customPortionGrams?: number | undefined;
  language: 'en' | 'de';
  showFPU?: boolean;
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  foods,
  carbUnit,
  customPortionGrams,
  language,
  showFPU = false
}) => {
  const [expandedFood, setExpandedFood] = useState<string | null>(null);
  const [selectedPortions, setSelectedPortions] = useState<{ [foodId: string]: string }>({});
  const [photoAttributions, setPhotoAttributions] = useState<{ [foodId: string]: { attribution: string; photoUrl: string; source?: string; author?: string; photographerProfileUrl?: string; directPhotoUrl?: string } }>({});
  const { toggleFavorite, isFavorite, deleteCustomFood, addIngredientToRecipe, deleteRecipe, clearCurrentRecipe, getRecipes, getCurrentRecipeIngredients, setCurrentLoadedRecipeId } = usePreferences();
  const [editingFoodId, setEditingFoodId] = useState<string | null>(null);
  const isGerman = language === 'de';

  // Load photo attributions for Swiss and USDA foods
  useEffect(() => {
    const loadAttributions = async () => {
      const attributions: { [foodId: string]: { attribution: string; photoUrl: string; source?: string; author?: string; photographerProfileUrl?: string; directPhotoUrl?: string } } = {};
      
      for (const food of foods) {
        if (food.id.startsWith('swiss_') || food.id.startsWith('usda_')) {
          try {
            const photoData = await getPhotoData(food.id);
            if (photoData) {
              attributions[food.id] = {
                attribution: photoData.attribution,
                photoUrl: photoData.photoUrl,
                source: photoData.source,
                author: photoData.author,
                photographerProfileUrl: photoData.photographerProfileUrl,
                directPhotoUrl: photoData.directPhotoUrl
              };
            }
          } catch (error) {
            console.log(`Failed to load attribution for ${food.id}:`, error);
          }
        }
      }
      
      setPhotoAttributions(attributions);
    };
    
    loadAttributions();
  }, [foods]);

  const renderPortion = (food: Food, portion: any, isCustom: boolean = false) => {
    const carbsForPortion = calculateCarbsForPortion(food.carbs, portion.grams);
    
    // Calculate FPU/FPE for this portion
    const fatForPortion = (food.fat * portion.grams) / 100;
    const proteinForPortion = (food.protein * portion.grams) / 100;
    const fpuResult = calculateFPU(fatForPortion, proteinForPortion);
    
    let baseDesc: string;
    
    if (isCustom) {
      baseDesc = isGerman ? `Eigene Menge (${portion.grams}g)` : `Custom serving (${portion.grams}g)`;
    } else if (portion.descEN === '100g' || portion.descDE === '100g') {
      // For synthetic 100g portions, show translated label
      baseDesc = isGerman ? '100g' : '100g';
    } else {
      // Use the translated portion description from database
      baseDesc = language === 'de' ? portion.descDE : (portion.descEN || portion.descDE || `${portion.grams}g`);
    }
    
    // Add gram weight to description if not custom and grams > 0 and not already a simple gram description
    const descForMatching = portion.descEN || portion.descDE || '';
    const portionDesc = !isCustom && portion.grams > 0 && !descForMatching.match(/^\d+g?$/) && descForMatching !== '100g'
      ? `${baseDesc} (${portion.grams}g)` 
      : baseDesc;
    
    const portionKey = isCustom ? 'custom' : `${portion.descEN || portion.descDE || portion.grams}_${portion.grams}g`;
    const isSelected = selectedPortions[food.id] === portionKey;
    
    const handlePortionClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isSelected) {
        // Deselect if clicking the same portion
        setSelectedPortions({});
      } else {
        // Select this portion and clear all others
        setSelectedPortions({
          [food.id]: portionKey
        });
      }
    };
    
    return (
      <div key={`${food.id}-${portionKey}`} className="portion-wrapper">
        <div 
          className={`portion-display ${isSelected ? 'selected' : ''}`}
          onClick={handlePortionClick}
        >
          <span className="portion-desc">{portionDesc}</span>
          <div className="portion-nutrients">
            <span className="portion-carbs">{formatCarbs(carbsForPortion, carbUnit, language)}</span>
            {showFPU && (
              <div className="portion-fpu">
                <span className="fpu-value">
                  {isGerman 
                    ? fpuResult.totalFPU.toFixed(1).replace('.', ',')
                    : fpuResult.totalFPU.toFixed(1)
                  } {isGerman ? 'FPE' : 'FPU'}
                </span>
              </div>
            )}
          </div>
        </div>
        {isSelected && (
          <div className="share-button-overlay">
            <button 
              className="recipe-button"
              onClick={(e) => {
                e.stopPropagation();
                handleAddToRecipe(food, portion, isCustom);
              }}
              title={isGerman ? 'Zum Rezept hinzufügen' : 'Add to recipe'}
            >
              <svg viewBox="0 0 24 24" className="recipe-icon">
                <path d="M8.1 13.34l2.83-2.83L3.91 3.5a4.008 4.008 0 0 0 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.20-1.10-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>
              </svg>
            </button>
            <button 
              className="share-button"
              onClick={(e) => {
                e.stopPropagation();
                handleShare(food, portion, isCustom);
              }}
              title={isGerman ? 'Teilen' : 'Share'}
            >
              <svg viewBox="0 0 24 24" className="share-icon">
                <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92S19.61 16.08 18 16.08z"/>
              </svg>
            </button>
          </div>
        )}
      </div>
    );
  };

  const toggleExpanded = (foodId: string) => {
    setExpandedFood(expandedFood === foodId ? null : foodId);
  };

  const handleFavoriteToggle = (foodId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent expanding the food card
    
    const wasFavorite = isFavorite(foodId);
    toggleFavorite(foodId);
    
    // If item was just favorited (not unfavorited), scroll to its new position at the top
    if (!wasFavorite) {
      // Use setTimeout to wait for the re-render and re-sorting
      setTimeout(() => {
        const foodCard = document.querySelector(`[data-food-id="${foodId}"]`) as HTMLElement;
        if (foodCard) {
          foodCard.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 100);
    }
  };

  const handleDeleteItem = (food: Food, foodName: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent expanding the food card
    
    const itemType = food.isRecipe ? (isGerman ? 'Rezept' : 'recipe') : (isGerman ? 'eigenen Eintrag' : 'custom entry');
    const message = isGerman 
      ? `Möchtest Du ${itemType} "${foodName}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`
      : `Are you sure you want to delete ${itemType} "${foodName}"? This action cannot be undone.`;
    
    if (window.confirm(message)) {
      if (food.isRecipe) {
        deleteRecipe(food.id);
      } else {
        deleteCustomFood(food.id);
      }
    }
  };

  const handleEditCustomFood = (foodId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent expanding the food card
    setEditingFoodId(foodId);
  };

  const handleLoadRecipe = (recipeId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent expanding the food card
    
    const recipes = getRecipes();
    const recipe = recipes.find(r => r.id === recipeId);
    
    if (!recipe) return;
    
    const currentIngredients = getCurrentRecipeIngredients();
    const hasActiveRecipe = currentIngredients.length > 0;
    
    // If there's no active recipe, load directly
    if (!hasActiveRecipe) {
      // Set this recipe as the currently loaded one
      setCurrentLoadedRecipeId(recipeId);
      
      // Add all ingredients from the saved recipe to current recipe
      recipe.ingredients.forEach(ingredient => {
        addIngredientToRecipe({
          foodId: ingredient.foodId,
          foodName: ingredient.foodName,
          portionName: ingredient.portionName,
          grams: ingredient.grams,
          carbs: ingredient.carbs,
          protein: ingredient.protein,
          fat: ingredient.fat,
          isCustomFood: ingredient.isCustomFood
        });
      });
      return;
    }
    
    // If there's an active recipe, ask for confirmation
    const message = isGerman 
      ? `Möchtest Du das Rezept "${recipe.name}" in das aktuelle Rezept laden? Dies wird alle aktuellen Zutaten ersetzen.`
      : `Do you want to load recipe "${recipe.name}" into the current recipe? This will replace all current ingredients.`;
    
    if (window.confirm(message)) {
      // Clear current recipe first
      clearCurrentRecipe();
      
      // Set this recipe as the currently loaded one
      setCurrentLoadedRecipeId(recipeId);
      
      // Add all ingredients from the saved recipe to current recipe
      recipe.ingredients.forEach(ingredient => {
        addIngredientToRecipe({
          foodId: ingredient.foodId,
          foodName: ingredient.foodName,
          portionName: ingredient.portionName,
          grams: ingredient.grams,
          carbs: ingredient.carbs,
          protein: ingredient.protein,
          fat: ingredient.fat,
          isCustomFood: ingredient.isCustomFood
        });
      });
    }
  };

  const handleAddToRecipe = (food: Food, portion: any, isCustom: boolean) => {
    const displayName = (language === 'de' ? food.nameDE : food.nameEN) || food.nameEN || food.nameDE;
    const carbsForPortion = calculateCarbsForPortion(food.carbs, portion.grams);
    const fatForPortion = (food.fat * portion.grams) / 100;
    const proteinForPortion = (food.protein * portion.grams) / 100;
    
    let portionDesc;
    if (isCustom) {
      portionDesc = isGerman ? `Eigene Menge (${portion.grams}g)` : `Custom serving (${portion.grams}g)`;
    } else if (portion.descEN === '100g' || portion.descDE === '100g') {
      portionDesc = '100g';
    } else {
      const baseDesc = language === 'de' ? portion.descDE : (portion.descEN || portion.descDE || `${portion.grams}g`);
      const descForMatching = portion.descEN || portion.descDE || '';
      portionDesc = portion.grams > 0 && !descForMatching.match(/^\d+g?$/) && descForMatching !== '100g'
        ? `${baseDesc} (${portion.grams}g)` 
        : baseDesc;
    }
    
    const ingredient = {
      foodId: food.id,
      foodName: displayName,
      portionName: portionDesc,
      grams: portion.grams,
      carbs: carbsForPortion,
      protein: proteinForPortion,
      fat: fatForPortion,
      isCustomFood: !!food.isCustom
    };
    
    addIngredientToRecipe(ingredient);
    
    // Clear selection after adding to recipe
    setSelectedPortions({});
  };

  const handleShare = async (food: Food, portion: any, isCustom: boolean) => {
    const displayName = (language === 'de' ? food.nameDE : food.nameEN) || food.nameEN || food.nameDE;
    const carbsForPortion = calculateCarbsForPortion(food.carbs, portion.grams);
    
    let portionDesc;
    if (isCustom) {
      portionDesc = isGerman ? `Eigene Menge (${portion.grams}g)` : `Custom serving (${portion.grams}g)`;
    } else if (portion.descEN === '100g' || portion.descDE === '100g') {
      portionDesc = '100g';
    } else {
      const baseDesc = language === 'de' ? portion.descDE : (portion.descEN || portion.descDE || `${portion.grams}g`);
      const descForMatching = portion.descEN || portion.descDE || '';
      portionDesc = portion.grams > 0 && !descForMatching.match(/^\d+g?$/) && descForMatching !== '100g'
        ? `${baseDesc} (${portion.grams}g)` 
        : baseDesc;
    }
    
    let shareText = `${displayName}, ${portionDesc}: ${formatCarbs(carbsForPortion, carbUnit, language)}`;
    
    // Add FPU/FPE information if enabled and available
    if (showFPU) {
      const fatForPortion = (food.fat * portion.grams) / 100;
      const proteinForPortion = (food.protein * portion.grams) / 100;
      const fpuResult = calculateFPU(fatForPortion, proteinForPortion);
      
      const fpuLabel = isGerman ? 'FPE' : 'FPU';
      const fpuValue = isGerman 
        ? fpuResult.totalFPU.toFixed(1).replace('.', ',')
        : fpuResult.totalFPU.toFixed(1);
      shareText += `, ${fpuValue} ${fpuLabel}`;
    }
    
    if (navigator.share) {
      try {
        await navigator.share({
          text: shareText
        });
      } catch (err) {
        // User cancelled or share failed
        console.log('Share cancelled');
      }
    } else {
      // Fallback for browsers without native share
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(shareText);
          alert(isGerman ? 'Text kopiert!' : 'Text copied!');
        } catch (err) {
          alert(isGerman ? 'Teilen nicht möglich' : 'Sharing not available');
        }
      } else {
        alert(isGerman ? 'Teilen nicht möglich' : 'Sharing not available');
      }
    }
  };


  return (
    <div className="search-results">
      {foods.map(food => {
        const displayName = language === 'de' ? food.nameDE : food.nameEN;
        
        // Skip foods that don't have a name in the current language
        if (!displayName) {
          return null;
        }
        const isExpanded = expandedFood === food.id;
        
        // Process portions to always show 100g first
        const originalPortions = [...food.portions];
        const has100gPortion = originalPortions.some(p => p.grams === 100);
        
        let portions;
        if (has100gPortion) {
          // If 100g portion exists, sort to show it first
          portions = originalPortions.sort((a, b) => {
            if (a.grams === 100) return -1;
            if (b.grams === 100) return 1;
            return 0;
          });
        } else {
          // If no 100g portion, add it as the first entry
          const hundredGramPortion = {
            descEN: '100g',
            descDE: '100g',
            grams: 100
          };
          portions = [hundredGramPortion, ...originalPortions];
        }
        
        return (
          <div 
            key={food.id} 
            className="food-card"
            data-food-id={food.id}
            onClick={() => toggleExpanded(food.id)}
          >
            <div className="food-card-header">
              <h3 className="food-name">
            <span className="food-name-text">
              {displayName}
            </span>
            <div className="food-actions">
                {food.isCustom && (
                  <>
                    {food.isRecipe ? (
                      <button 
                        className="load-button"
                        onClick={(e) => handleLoadRecipe(food.id, e)}
                        title={isGerman ? 'Rezept laden' : 'Load recipe'}
                      >
                        <svg viewBox="0 0 24 24" className="load-icon">
                          <path d="M8.1 13.34l2.83-2.83L3.91 3.5a4.008 4.008 0 0 0 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.20-1.10-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>
                        </svg>
                      </button>
                    ) : (
                      <button 
                        className="edit-button"
                        onClick={(e) => handleEditCustomFood(food.id, e)}
                        title={isGerman ? 'Eigenen Eintrag bearbeiten' : 'Edit custom entry'}
                      >
                        <svg viewBox="0 0 24 24" className="edit-icon">
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                        </svg>
                      </button>
                    )}
                    <button 
                      className="delete-button"
                      onClick={(e) => handleDeleteItem(food, displayName, e)}
                      title={food.isRecipe 
                        ? (isGerman ? 'Rezept löschen' : 'Delete recipe')
                        : (isGerman ? 'Eigenen Eintrag löschen' : 'Delete custom entry')
                      }
                    >
                      <svg viewBox="0 0 24 24" className="delete-icon">
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                      </svg>
                    </button>
                  </>
                )}
                <button 
                  className={`favorite-button ${isFavorite(food.id) ? 'favorited' : ''}`}
                  onClick={(e) => handleFavoriteToggle(food.id, e)}
                  title={isFavorite(food.id) 
                    ? (isGerman ? 'Aus Favoriten entfernen' : 'Remove from favorites')
                    : (isGerman ? 'Zu Favoriten hinzufügen' : 'Add to favorites')
                  }
                >
                  <svg viewBox="0 0 24 24" className="star-icon">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                  </svg>
                </button>
            </div>
          </h3>
            </div>
            
            <div className="food-card-main">
              <FoodImage 
                ean={food.ean}
                foodId={food.id}
                foodName={displayName}
                language={language}
                size="400"
                className="food-card-image"
              />
              <div className="portions-container">
            {/* Custom portion first if provided and entered */}
            {customPortionGrams !== undefined && customPortionGrams > 0 && (
              renderPortion(food, { grams: customPortionGrams }, true)
            )}
            
            {/* Standard portions - show first 5 + 1 extra if only 6 total, or all if expanded */}
            {(() => {
              const displayLimit = portions.length === 6 ? 6 : 5;
              const portionsToShow = isExpanded ? portions : portions.slice(0, displayLimit);
              return portionsToShow.map((portion) => 
                renderPortion(food, portion, false)
              );
            })()}
            
            {!isExpanded && portions.length > 6 && (
              <div className="more-portions">
                +{portions.length - 5} {isGerman ? 'weitere Portionen' : 'more portions'}
              </div>
            )}
            
            {food.source && (
              <div className="card-source-attribution">
                <div className="source-line">
                  {isGerman ? 'Nährwerte: ' : 'Nutritional values: '}
                  {getSourceUrl(food) ? (
                    <a href={getSourceUrl(food)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                      {isGerman && food.sourceDE ? food.sourceDE : food.source}
                    </a>
                  ) : (
                    isGerman && food.sourceDE ? food.sourceDE : food.source
                  )}
                </div>
                {food.ean && (
                  <div className="photo-line">
                    {language === 'de' ? 'Foto: © Open Food Facts Mitwirkende / CC BY‑SA 3.0' : 'Photo: © Open Food Facts contributors / CC BY‑SA 3.0'}
                  </div>
                )}
                {photoAttributions[food.id] && (
                  <div className="photo-line">
                    {photoAttributions[food.id].source?.toLowerCase().includes('unsplash') ? (
                      // Special handling for Unsplash with three separate links
                      <span>
                        <a 
                          href={photoAttributions[food.id].directPhotoUrl || photoAttributions[food.id].photoUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          onClick={(e) => e.stopPropagation()}
                        >
                          {language === 'de' ? 'Foto' : 'Photo'}
                        </a>
                        {': '}
                        <a 
                          href={photoAttributions[food.id].photographerProfileUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          onClick={(e) => e.stopPropagation()}
                        >
                          {photoAttributions[food.id].author}
                        </a>
                        {' on '}
                        <a 
                          href="https://unsplash.com/?utm_source=zuckerrechner&utm_medium=referral" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          onClick={(e) => e.stopPropagation()}
                        >
                          Unsplash
                        </a>
                      </span>
                    ) : photoAttributions[food.id].source?.toLowerCase().includes('freefoodphotos') ? (
                      // Special handling for FreeFoodPhotos
                      <span>
                        <a 
                          href={photoAttributions[food.id].photoUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          onClick={(e) => e.stopPropagation()}
                        >
                          {language === 'de' ? 'Foto' : 'Photo'}
                        </a>
                        {': '}
                        <a 
                          href="https://freefoodphotos.com" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          onClick={(e) => e.stopPropagation()}
                        >
                          FreeFoodPhotos
                        </a>
                      </span>
                    ) : (
                      // Standard single-link attribution for other sources  
                      <span>
                        <a 
                          href={photoAttributions[food.id].photoUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          onClick={(e) => e.stopPropagation()}
                        >
                          {language === 'de' ? 'Foto' : 'Photo'}
                        </a>
                        {': ' + photoAttributions[food.id].attribution}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
            
              </div>
            </div>
          </div>
        );
      })}
      
      {/* Edit Modal */}
      <AddCustomFoodModal 
        isOpen={!!editingFoodId}
        onClose={() => setEditingFoodId(null)}
        editingFoodId={editingFoodId || undefined}
      />
    </div>
  );
};